import {useCallback} from 'react';

import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';
import type {SaveContext} from 'src/hooks/use-common-data-hook';
import type {TempLimitsSettings} from 'src/modules/usage-limits/typedef';
import type {TempLimit} from 'src/openapi/admin/models/TempLimit';
import type {ErrorPayload, SuccessPayload} from 'src/typedef';

const NO_TEMP_LIMITS_ERROR =
    'At least one temporary limit must be specified';

const NO_INITIAL_LIMITS_ERROR =
    'No initial limits found';

export const DEFAULT_TEMP_LIMITS: TempLimitsSettings = {
    expiresOn: '',
    limits: [],
};

type ApiResponse<T = unknown> = {
    type: string,
    payload: SuccessPayload<T> | ErrorPayload,
};

type Props = {
    getTempLimits: (
        signal: AbortSignal,
    ) => Promise<ApiResponse<TempLimit[]>>,
    saveTempLimit: (
        limits: TempLimit[],
        expiresOn: string,
        signal: AbortSignal,
        options?: SaveOptions,
    ) => Promise<ApiResponse>,
    removeTempLimit: (
        limitIds: string[],
        signal: AbortSignal,
        options?: SaveOptions,
    ) => Promise<ApiResponse>,
};

export const useMcpTemporaryLimits = ({
    getTempLimits,
    saveTempLimit,
    removeTempLimit,
}: Props) => {
    const getDispatchAction = useCallback(
        async (signal: AbortSignal) => {
            const response = await getTempLimits(signal);

            if (response.payload.success) {
                const limits =
                    response.payload.data || [];
                return Promise.resolve({
                    type: '',
                    payload: {
                        success: true,
                        error: false,
                        data: {
                            limits,
                            expiresOn:
                                limits
                                    .find(Boolean)
                                    ?.temporaryDate
                                    || '',
                        },
                    } as SuccessPayload<TempLimitsSettings>,
                });
            }

            const status = response.payload.data?.status;
            if (status === 404) {
                return Promise.resolve({
                    type: '',
                    payload: {
                        success: true,
                        error: false,
                        data: DEFAULT_TEMP_LIMITS,
                    } as SuccessPayload<TempLimitsSettings>,
                });
            }

            return Promise.resolve(
                {type: '', payload: response.payload},
            );
        },
        [getTempLimits],
    );

    const saveDispatchAction = useCallback(
        async (
            settings: TempLimitsSettings,
            signal: AbortSignal,
            extra: (SaveOptions & {initialLimits?: TempLimit[]}) | undefined,
            {initialData, setInitialData}: SaveContext<TempLimitsSettings>,
        ) => {
            const {initialLimits, reason} = extra || {};
            const {limits, expiresOn} = settings;

            if (expiresOn && !limits.length) {
                return Promise.resolve({
                    type: '',
                    payload: {
                        data: {
                            message: NO_TEMP_LIMITS_ERROR,
                        },
                        error: true,
                        success: false,
                    } as ErrorPayload,
                });
            }

            if (!initialLimits) {
                return Promise.resolve({
                    type: '',
                    payload: {
                        data: {
                            message:
                                NO_INITIAL_LIMITS_ERROR,
                        },
                        success: false,
                        error: true,
                    } as ErrorPayload,
                });
            }

            if (expiresOn) {
                const removedLimits = initialData.limits.filter(
                    initialLimit => !limits.find(
                        limit =>
                            limit.limitId
                            === initialLimit.limitId,
                    ),
                );

                const savedSettings = {
                    ...initialData,
                    limits: initialData.limits.filter(initial => (
                        limits.some(limit => limit.limitId === initial.limitId)
                    )),
                };
                if (!isDeepEqual({prev: savedSettings, current: settings})) {
                    const saveResponse = await saveTempLimit(
                        limits, expiresOn, signal, {reason},
                    );
                    if (saveResponse.payload.error) {
                        return Promise.resolve({
                            type: '',
                            payload: saveResponse.payload,
                        });
                    }
                    setInitialData({...settings, limits: [...limits, ...removedLimits]});
                }

                if (removedLimits.length) {
                    const removeResponse =
                        await removeTempLimit(
                            removedLimits.map(
                                ({limitId}) => limitId,
                            ),
                            signal, {reason},
                        );
                    if (removeResponse.payload.error) {
                        return Promise.resolve({
                            type: '',
                            payload: removeResponse.payload,
                        });
                    }
                }
            } else {
                const removeResponse =
                    await removeTempLimit(
                        initialData.limits.map(
                            ({limitId}) => limitId,
                        ),
                        signal, {reason},
                    );
                if (removeResponse.payload.error) {
                    return Promise.resolve({
                        type: '',
                        payload: removeResponse.payload,
                    });
                }
            }

            return Promise.resolve({
                type: '',
                payload: {
                    data: undefined,
                    success: true,
                    error: false,
                } as SuccessPayload<void>,
            });
        },
        [saveTempLimit, removeTempLimit],
    );

    const hook = useCommonDataHook({
        initValue: DEFAULT_TEMP_LIMITS,
        getDispatchAction,
        saveDispatchAction,
    });
    const {saveChanges: save} = hook;
    const saveChanges = useCallback((initialLimits?: TempLimit[], options?: SaveOptions) => (
        save({initialLimits, ...options})
    ), [save]);

    return {...hook, saveChanges};
};
